/**
 * GIF Service - Tenor API v2 Integration
 *
 * Google's free GIF API for chat integration
 * Docs: https://developers.google.com/tenor/guides/quickstart
 *
 * Features:
 * - Search GIFs by keyword
 * - Trending GIFs
 * - Multiple formats (webp, gif, mp4)
 * - Cursor-based pagination
 */

import apiClient from './api.client';

export interface GifFormat {
  url: string;
  width: number;
  height: number;
  size?: number;
}

export interface GifItem {
  id: string;
  title: string;
  slug: string;
  url: string;
  formats: {
    gif?: GifFormat;
    webp?: GifFormat;
    mp4?: GifFormat;
    preview?: GifFormat;
    tiny?: GifFormat;
  };
}

export interface GifSearchResponse {
  data: GifItem[];
  page: number;
  per_page: number;
  total?: number;
}

// Tenor API response types (internal)
interface TenorMediaFormat {
  url: string;
  dims: [number, number];
  size: number;
  duration?: number;
}

interface TenorResult {
  id: string;
  title: string;
  media_formats: {
    gif?: TenorMediaFormat;
    mediumgif?: TenorMediaFormat;
    tinygif?: TenorMediaFormat;
    nanogif?: TenorMediaFormat;
    webp?: TenorMediaFormat;
    tinywebp?: TenorMediaFormat;
    nanowebp?: TenorMediaFormat;
    mp4?: TenorMediaFormat;
    tinymp4?: TenorMediaFormat;
  };
  content_description?: string;
  url: string;
}

interface TenorResponse {
  results: TenorResult[];
  next: string;
}

class GifService {
  private cache: Map<string, { data: GifSearchResponse; timestamp: number }> = new Map();
  private cacheExpiry = 5 * 60 * 1000; // 5 minutes
  private nextPositions: Map<string, string> = new Map();

  /**
   * Search for GIFs by keyword.
   * audit #50: goes through the backend proxy so the Tenor key never reaches the
   * client. The proxy returns Tenor's native { results, next } shape.
   */
  async search(query: string, page = 1, perPage = 20): Promise<GifSearchResponse> {
    const cacheKey = `search:${query}:${page}:${perPage}`;
    const cached = this.getFromCache(cacheKey);
    if (cached) return cached;

    try {
      const posKey = `search:${query}`;
      const pos = page > 1 ? this.nextPositions.get(posKey) : undefined;
      const response = await apiClient.get<TenorResponse>('/settings/gif/search', {
        params: { q: query, limit: perPage, ...(pos ? { pos } : {}) },
      });
      const data = response.data;

      if (data.next) {
        this.nextPositions.set(posKey, data.next);
      }

      const result = this.transformResponse(data, page, perPage);
      this.setCache(cacheKey, result);
      return result;
    } catch (error) {
      console.error('GIF search failed:', error);
      throw error;
    }
  }

  /**
   * Get trending GIFs (via the backend proxy — see search()).
   */
  async trending(page = 1, perPage = 20): Promise<GifSearchResponse> {
    const cacheKey = `trending:${page}:${perPage}`;
    const cached = this.getFromCache(cacheKey);
    if (cached) return cached;

    try {
      const posKey = 'trending';
      const pos = page > 1 ? this.nextPositions.get(posKey) : undefined;
      const response = await apiClient.get<TenorResponse>('/settings/gif/trending', {
        params: { limit: perPage, ...(pos ? { pos } : {}) },
      });
      const data = response.data;

      if (data.next) {
        this.nextPositions.set(posKey, data.next);
      }

      const result = this.transformResponse(data, page, perPage);
      this.setCache(cacheKey, result);
      return result;
    } catch (error) {
      console.error('Trending GIFs fetch failed:', error);
      throw error;
    }
  }

  /**
   * Transform Tenor API response to our format
   */
  private transformResponse(
    response: TenorResponse,
    page: number,
    perPage: number
  ): GifSearchResponse {
    const items: GifItem[] = (response.results || []).map((item) => {
      const mf = item.media_formats;

      return {
        id: item.id,
        title: item.content_description || item.title || '',
        slug: item.id,
        url: mf.gif?.url || mf.mediumgif?.url || '',
        formats: {
          gif: mf.gif ? { url: mf.gif.url, width: mf.gif.dims[0], height: mf.gif.dims[1], size: mf.gif.size } : undefined,
          webp: mf.webp ? { url: mf.webp.url, width: mf.webp.dims[0], height: mf.webp.dims[1], size: mf.webp.size } : undefined,
          mp4: mf.mp4 ? { url: mf.mp4.url, width: mf.mp4.dims[0], height: mf.mp4.dims[1], size: mf.mp4.size } : undefined,
          preview: mf.tinywebp
            ? { url: mf.tinywebp.url, width: mf.tinywebp.dims[0], height: mf.tinywebp.dims[1], size: mf.tinywebp.size }
            : mf.tinygif
              ? { url: mf.tinygif.url, width: mf.tinygif.dims[0], height: mf.tinygif.dims[1], size: mf.tinygif.size }
              : undefined,
          tiny: mf.nanowebp
            ? { url: mf.nanowebp.url, width: mf.nanowebp.dims[0], height: mf.nanowebp.dims[1], size: mf.nanowebp.size }
            : mf.nanogif
              ? { url: mf.nanogif.url, width: mf.nanogif.dims[0], height: mf.nanogif.dims[1], size: mf.nanogif.size }
              : undefined,
        },
      };
    });

    return {
      data: items,
      page,
      per_page: perPage,
    };
  }

  /**
   * Get the best format URL for a GIF (prefer webp for performance)
   */
  getBestFormat(gif: GifItem, preferPreview = false): string {
    if (preferPreview && gif.formats.preview?.url) {
      return gif.formats.preview.url;
    }
    if (preferPreview && gif.formats.tiny?.url) {
      return gif.formats.tiny.url;
    }
    // Prefer webp for better compression, fallback to gif
    return gif.formats.webp?.url || gif.formats.gif?.url || gif.url;
  }

  /**
   * Get preview thumbnail URL
   */
  getPreviewUrl(gif: GifItem): string {
    return gif.formats.preview?.url || gif.formats.tiny?.url || this.getBestFormat(gif);
  }

  /**
   * Cache management
   */
  private getFromCache(key: string): GifSearchResponse | null {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.cacheExpiry) {
      return cached.data;
    }
    return null;
  }

  private setCache(key: string, data: GifSearchResponse): void {
    this.cache.set(key, { data, timestamp: Date.now() });

    // Clean old cache entries
    if (this.cache.size > 50) {
      const oldest = Array.from(this.cache.entries())
        .sort((a, b) => a[1].timestamp - b[1].timestamp)
        .slice(0, 10);
      oldest.forEach(([k]) => this.cache.delete(k));
    }
  }

  /**
   * Clear cache
   */
  clearCache(): void {
    this.cache.clear();
    this.nextPositions.clear();
  }
}

// Export singleton instance
export const gifService = new GifService();

// Convenience functions
export async function searchGifs(query: string, page?: number, perPage?: number) {
  return gifService.search(query, page, perPage);
}

export async function getTrendingGifs(page?: number, perPage?: number) {
  return gifService.trending(page, perPage);
}
